import { scaleSequential } from 'd3-scale';
import { interpolateBlues } from 'd3-scale-chromatic';
import { format, getDay, getDaysInMonth } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';

/**
 * D3YearlyHeatmap - A responsive, accessible, yearly calendar heatmap (like GitHub's) using D3.
 *
 * Props:
 *   - year: number (e.g. 2024)
 *   - data: Array<{ date: string (yyyy-MM-dd), count: number, amount: number }>
 *   - colorScale: (count: number, max: number) => string OR string[]
 *   - startMonth: number (0=Jan, 11=Dec, default 0)
 *   - numMonths: number (default 12)
 *   - cellSize: number (default 16)
 *   - className/style: optional
 *   - highlightBlock?: (d: D3YearlyHeatmapDatum) => boolean
 *   - disableBlock?: (d: D3YearlyHeatmapDatum) => boolean
 */
export interface D3YearlyHeatmapDatum {
  date: string; // yyyy-MM-dd
  count: number;
  amount: number;
}

export interface D3YearlyHeatmapProps {
  year: number;
  data: Array<D3YearlyHeatmapDatum>;
  colorScale?: ((count: number, max: number) => string) | Array<string>;
  startMonth?: number; // 0=Jan
  numMonths?: number;
  cellSize?: number;
  className?: string;
  style?: React.CSSProperties;
  /**
   * If provided, blocks for which this returns true will be highlighted (e.g., today)
   */
  highlightBlock?: (d: D3YearlyHeatmapDatum) => boolean;
  /**
   * If provided, blocks for which this returns true will be disabled (faded, unclickable, etc)
   */
  disableBlock?: (d: D3YearlyHeatmapDatum) => boolean;
  /** Studio currency symbol from `/currency` (e.g. £, $) */
  currencySymbol?: string;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const WEEK_DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const CELL_GAP = 3; // px

const TOP_PADDING = 16; // px

const D3YearlyHeatmap: React.FC<D3YearlyHeatmapProps> = ({
  year,
  data,
  colorScale,
  startMonth = 0,
  numMonths = 12,
  cellSize = 16,
  className,
  style,
  highlightBlock,
  disableBlock,
  currencySymbol = '$',
}) => {
  // Tooltip state
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    content: string;
  } | null>(null);

  // Build a map for quick lookup
  const dataMap = useMemo(() => {
    const map: Record<string, D3YearlyHeatmapDatum> = {};
    for (const d of data) map[d.date] = d;
    return map;
  }, [data]);

  // Build days grouped by month
  const monthsData = useMemo(() => {
    const months: Array<{
      month: number;
      days: Array<{
        date: Date;
        iso: string;
        count: number;
        amount: number;
      } | null>;
    }> = [];
    for (let m = startMonth; m < Math.min(startMonth + numMonths, 12); m++) {
      const days: Array<{
        date: Date;
        iso: string;
        count: number;
        amount: number;
      } | null> = [];
      const daysInMonth = getDaysInMonth(new Date(year, m));
      const firstDay = new Date(year, m, 1);
      const firstWeekday = getDay(firstDay); // 0=Sun
      // Pad with nulls for days before the 1st
      for (let i = 0; i < firstWeekday; i++) {
        days.push(null);
      }
      for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(year, m, d);
        const iso = format(date, 'yyyy-MM-dd');
        const datum = dataMap[iso];
        days.push({
          date,
          iso,
          count: datum?.count ?? 0,
          amount: datum?.amount ?? 0,
        });
      }
      months.push({ month: m, days });
    }
    return months;
  }, [year, dataMap, startMonth, numMonths]);

  // Find max count for color scale
  const maxCount = useMemo(() => {
    return Math.max(1, ...data.map((d) => d.count));
  }, [data]);

  // Layout: each month is a block, 7 columns, rows = ceil(days/7)
  const MONTH_BLOCK_GAP = 24; // px between months
  const monthBlockWidth = 7 * (cellSize + CELL_GAP);
  const monthBlockHeight = 6 * (cellSize + CELL_GAP); // max 6 rows
  const WEEK_LABEL_GAP = 28; // px for weekday labels
  const width =
    WEEK_LABEL_GAP +
    monthsData.length * monthBlockWidth +
    (monthsData.length - 1) * MONTH_BLOCK_GAP;
  const height = monthBlockHeight + 28 + TOP_PADDING; // 28px for label + top padding

  // D3 color scale: blue gradient
  const d3ColorScale = scaleSequential(interpolateBlues).domain([
    0,
    maxCount || 1,
  ]);

  // Color scale function (preserve custom if provided)
  const getColor = (count: number) => {
    if (typeof colorScale === 'function') return colorScale(count, maxCount);
    if (Array.isArray(colorScale)) {
      const idx = Math.ceil((count / maxCount) * (colorScale.length - 1));
      return colorScale[idx] ?? colorScale[colorScale.length - 1] ?? '#1565c0';
    }
    // Use d3 color scale for blue gradient
    return count > 0 ? d3ColorScale(count) : '#fff';
  };

  // Tooltip handlers
  const handleMouseOver = useCallback(
    (e: React.MouseEvent, d: (typeof monthsData)[0]['days'][0]) => {
      if (!d) return;
      setTooltip({
        x: e.clientX + 12,
        y: e.clientY - 8,
        content: `${format(d.date, 'EEE, MMM d, yyyy')}\n${d.count} payment${d.count === 1 ? '' : 's'}\n${currencySymbol}${d.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      });
    },
    [currencySymbol],
  );
  const handleFocus = useCallback(
    (
      e: React.FocusEvent<SVGRectElement>,
      d: (typeof monthsData)[0]['days'][0],
    ) => {
      if (!d) return;
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltip({
        x: rect.left + rect.width / 2 + window.scrollX,
        y: rect.top + window.scrollY - 8,
        content: `${format(d.date, 'EEE, MMM d, yyyy')}\n${d.count} payment${d.count === 1 ? '' : 's'}\n${currencySymbol}${d.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      });
    },
    [currencySymbol],
  );
  const handleBlur = useCallback(() => {
    setTooltip(null);
  }, []);

  // Today's date (yyyy-MM-dd)
  // const todayIso = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);

  return (
    <div
      className={className}
      style={{ position: 'relative', width: '100%', ...style }}
    >
      <svg
        width='100%'
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{ display: 'block', width: '100%', height }}
        role='img'
        aria-label={`Yearly payment heatmap for ${year}`}
      >
        {/* Month blocks */}
        {monthsData.map((month, mi) => (
          <g
            key={month.month}
            transform={`translate(${WEEK_LABEL_GAP + mi * (monthBlockWidth + MONTH_BLOCK_GAP)},${TOP_PADDING})`}
          >
            {/* Weekday indicators (above the month block, left to right) */}
            {WEEK_DAYS.map((wd, i) => (
              <text
                key={`wd-${mi}-${i}`}
                x={i * (cellSize + CELL_GAP) + cellSize / 2}
                y={cellSize * 0.8}
                textAnchor='middle'
                fontSize={11}
                fill='#aaa'
              >
                {wd}
              </text>
            ))}
            {/* Month grid */}
            {month.days.map((d, i) => {
              const row = Math.floor(i / 7);
              const col = i % 7;
              if (!d) {
                // Render an invisible rect for spacing
                return (
                  <rect
                    key={`empty-${i}`}
                    x={col * (cellSize + CELL_GAP)}
                    y={cellSize + row * (cellSize + CELL_GAP)}
                    width={cellSize}
                    height={cellSize}
                    fill='transparent'
                  />
                );
              }
              // Compose datum for callbacks (date as string)
              const datum: D3YearlyHeatmapDatum = {
                date: d.iso,
                count: d.count,
                amount: d.amount,
              };
              // Highlight and disable logic (new API)
              let extraStyle: React.CSSProperties = {};
              const extraProps: Partial<React.SVGProps<SVGRectElement>> = {};
              const isHighlighted = highlightBlock?.(datum) ?? false;
              const isDisabled = disableBlock?.(datum) ?? false;
              if (isHighlighted) {
                extraStyle = {
                  ...extraStyle,
                  stroke: '#ff9800', // orange
                  strokeWidth: 3,
                  filter: 'drop-shadow(0 0 6px #ff980088)',
                  zIndex: 2,
                };
              }
              if (isDisabled) {
                extraStyle = {
                  ...extraStyle,
                  opacity: 0.4,
                  cursor: 'not-allowed',
                };
                extraProps.pointerEvents = 'none';
              }
              return (
                <rect
                  key={d.iso}
                  x={col * (cellSize + CELL_GAP)}
                  y={cellSize + row * (cellSize + CELL_GAP)}
                  width={cellSize}
                  height={cellSize}
                  rx={3}
                  fill={getColor(d.count)}
                  stroke='#d1d5db'
                  strokeWidth={1}
                  tabIndex={0}
                  aria-label={`${format(d.date, 'EEEE, MMMM d, yyyy')}: ${d.count} payment${d.count === 1 ? '' : 's'}, ${currencySymbol}${d.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  style={{
                    cursor: d.count > 0 ? 'pointer' : 'default',
                    transition: 'fill 0.2s',
                    ...extraStyle,
                  }}
                  onMouseOver={
                    isDisabled
                      ? undefined
                      : (e) => {
                          handleMouseOver(e, d);
                        }
                  }
                  onFocus={
                    isDisabled
                      ? undefined
                      : (e) => {
                          handleFocus(e, d);
                        }
                  }
                  onMouseOut={isDisabled ? undefined : handleBlur}
                  onBlur={isDisabled ? undefined : handleBlur}
                  {...extraProps}
                />
              );
            })}
            {/* Month label */}
            <text
              x={monthBlockWidth / 2}
              y={monthBlockHeight + 20}
              textAnchor='middle'
              fontSize={15}
              fill='#ccc'
              fontWeight={400}
            >
              {MONTHS[month.month]}
            </text>
          </g>
        ))}
      </svg>
      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            zIndex: 1000,
            pointerEvents: 'none',
            background: 'white',
            border: '1px solid #d1d5db',
            borderRadius: 4,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            padding: '6px 10px',
            fontSize: 12,
            whiteSpace: 'pre',
            color: '#222',
          }}
        >
          {tooltip.content}
        </div>
      )}
    </div>
  );
};

export default D3YearlyHeatmap;
